import React, {
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { GridFilterItem } from "@mui/x-data-grid";
import { debounce } from "lodash";

import { QUOTES } from "helpers/apis";
import useAxios from "helpers/axios";
import { IQuote } from "types/quote";

export interface IQuoteContext {
  currentPage: number;
  loading: boolean;
  panelTransferred: boolean;
  refetch: (currentPage: number, filter?: GridFilterItem) => void;
  quotes: IQuote[];
  totalCount: number;
  setCurrentPage: Function;
  setPanelTransferred: React.Dispatch<React.SetStateAction<boolean>>;
  setQuotes: React.Dispatch<React.SetStateAction<IQuote[]>>;
}

export const QuoteContext = createContext<IQuoteContext>({} as IQuoteContext);

export function QuoteProvider({
  children,
}: {
  children: ReactNode;
}): JSX.Element {
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [quotes, setQuotes] = useState<IQuote[]>([]);
  const [panelTransferred, setPanelTransferred] = useState(false);
  const { fetchData, loading } = useAxios({
    config: { url: QUOTES },
    callOnInit: false,
  });
  const { current: debouncedFetch } = useRef(
    debounce((currentPage, filter?: GridFilterItem) => {
      fetchData(
        {
          params: {
            pageNumber: currentPage,
            pageSize: 20,
            ...(filter
              ? {
                  filter: JSON.stringify({
                    id: filter.id,
                    column_field: filter.field,
                    operator_value: filter.operator,
                    value: filter.value,
                  }),
                }
              : {}),
          },
        },
        ({
          response: { data, totalCount: newTotalCount },
        }: {
          response: {
            data: Array<IQuote>;
            totalCount: number;
          };
        }) => {
          if (Array.isArray(data)) {
            setQuotes(data);
            setTotalCount(newTotalCount);
          } else {
            setQuotes([]);
            setTotalCount(0);
            setCurrentPage(1);
          }
        }
      );
    }, 500)
  );

  const memoedValue = useMemo(
    () => ({
      currentPage,
      loading,
      panelTransferred,
      quotes,
      refetch: debouncedFetch,
      totalCount,
      setCurrentPage,
      setPanelTransferred,
      setQuotes,
    }),
    [currentPage, debouncedFetch, loading, panelTransferred, quotes, totalCount]
  );

  useEffect(() => {
    if (debouncedFetch) {
      debouncedFetch(currentPage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedFetch]);

  return (
    <QuoteContext.Provider value={memoedValue}>
      {children}
    </QuoteContext.Provider>
  );
}

export default function useQuote() {
  return useContext(QuoteContext);
}
